import React, {useCallback, useContext, useMemo, useState} from 'react';

import {SectionFilter} from 'src/components/section-filter';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {SortDirection as SortDirections} from 'src/openapi/admin/models/SortDirection';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {FormattedDate} from '@pb/kit-component-formatters';
import {Group, Item} from '@pb/kit-component-group';
import {Icons} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import Label from '@pb/kit-component-label';
import {PaginationStripe} from '@pb/kit-component-pagination';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';
import {LocaleContext} from '@pb/kit-context-locale';

import {DownloadNewsletterHistory} from './download-newsletter-history';
import {NewsletterHistoryFiltersBody} from './newsletter-history-filters-body';

import type {PaginationChangeHandler} from '@pb/kit-component-pagination';
import type {Dispatch, SetStateAction} from 'react';
import type {DictionaryTree} from 'src/openapi/admin/models/DictionaryTree';
import type {Error} from 'src/openapi/admin/models/Error';
import type {NewsletterHistoryFilters} from 'src/openapi/admin/models/NewsletterHistoryFilters';
import type {NewsletterSendEvent} from 'src/openapi/admin/models/NewsletterSendEvent';
import type {SortDirection} from 'src/openapi/admin/models/SortDirection';

type Props = {
    isNewsletterHistoryLoading: boolean,
    newsletterHistoryLoadingError: Error | null,

    newsletterHistory: NewsletterSendEvent[],
    sortDirection: SortDirection,
    setSortDirection: Dispatch<SetStateAction<SortDirection>>,
    filters: NewsletterHistoryFilters,

    applyFilters: (filters: NewsletterHistoryFilters) => void,
    clearFilters: () => NewsletterHistoryFilters,

    newsletterDictionary: DictionaryTree[],
    availableNewsletterTypes: string[],
};

export const NewsletterHistoryView = (props: Props) => {
    const {locale} = useContext(LocaleContext);
    const {getMonthPeriod} = useDateTime();
    const [pageSize, setPageSize] = useState<number>(5);
    const [currentPage, setCurrentPage] = useState<number>(1);

    const {
        isNewsletterHistoryLoading,
        newsletterHistoryLoadingError,

        newsletterHistory,
        sortDirection,
        setSortDirection,
        filters,

        applyFilters,
        clearFilters,
        newsletterDictionary,
        availableNewsletterTypes,
    } = props;

    const newsletterLabels = useMemo(() =>
        Object.fromEntries(newsletterDictionary.flatMap(item =>
            item.children?.map(c => [c.id, c.description]) ?? [[item.id, item.description]],
        )),
    [newsletterDictionary]);

    const onPaginationChange = useCallback<PaginationChangeHandler>(pagination => {
        setCurrentPage(pagination.currentPage);
        setPageSize(pagination.pageSize);
    }, [setPageSize, setCurrentPage]);

    const changeSortDirection = useCallback(() => {
        setSortDirection(prevState => (prevState === SortDirections.ASC
            ? SortDirections.DESC
            : SortDirections.ASC
        ));
    }, [setSortDirection]);


    return (
        <ServerResponseHandler serverError={newsletterHistoryLoadingError} smallView={true}>
            <div style={{width: 650}}>
                <Group justify="space-between">
                    <Label>Newsletter sending history</Label>
                    <Group>
                        <DownloadNewsletterHistory
                            newsletterLabels={newsletterLabels}
                            history={{
                                request: {
                                    filters,
                                    sortDirection,
                                },
                                data: newsletterHistory,
                            }}
                        />
                        <SectionFilter<NewsletterHistoryFilters>
                            maxHeight={900}
                            onApply={applyFilters}
                            onClear={clearFilters}
                            filters={filters}
                            defaultFilters={{
                                newsletterTypes: availableNewsletterTypes,
                                period: getMonthPeriod(),
                            }}>
                            {(filter, onChange) => (
                                <NewsletterHistoryFiltersBody {...{filter, onChange, newsletterDictionary}}/>
                            )}
                        </SectionFilter>
                    </Group>
                </Group>
                <BoxLoading loading={isNewsletterHistoryLoading}>
                    <Group direction="v">
                        <Table borderedGrid={true}>
                            <Head>
                                <Cell>Newsletter</Cell>
                                <Cell>
                                    <Group justify="space-between">
                                        <Item>Date Received</Item>
                                        <Item>
                                            <BasicIconButton
                                                iconProps={{type: sortDirection === SortDirections.DESC
                                                    ? Icons.LONG_ARROW_DOWN
                                                    : Icons.LONG_ARROW_UP,
                                                }}
                                                onClick={changeSortDirection}
                                            />
                                        </Item>
                                    </Group>
                                </Cell>
                            </Head>
                            <Body>
                                {newsletterHistory
                                    .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                                    .map(({sendingDate, newsletterType}, i) => (
                                        <Row key={i}>
                                            <Cell>{newsletterLabels[newsletterType] ?? newsletterType}</Cell>
                                            <Cell>{FormattedDate.format({
                                                value: sendingDate,
                                                format: mapDateFormat(locale.dateFormat, 'WORD', 'DATE_TIME'),
                                            }) || ''}
                                            </Cell>
                                        </Row>
                                    ))}
                                {!newsletterHistory.length && (
                                    <Row>
                                        <Cell colSpan={2} className="text-align_center">
                                            No results have been found
                                        </Cell>
                                    </Row>
                                )}
                            </Body>
                        </Table>
                        <PaginationStripe
                            totalCount={newsletterHistory.length}
                            currentPage={currentPage}
                            pageSize={pageSize}
                            onPaginationChange={onPaginationChange}
                            sizes={[5, 10]}
                        />
                    </Group>
                </BoxLoading>
            </div>
        </ServerResponseHandler>
    );
};
